import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import { Shield, Bell, Download, Trash2, Vote, RotateCcw, HelpCircle, Lightbulb, KeyRound, Laptop, Pencil, CreditCard, Gift, MessageSquarePlus, Share, LogOut } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ContextualHelp from '@/components/ContextualHelp';
import TwoFactorSetup from '@/components/TwoFactorSetup';
import TrustedDevicesList from '@/components/TrustedDevicesList';
import EmailPreferences from '@/components/EmailPreferences';
import { usePricing } from '@/hooks/usePricing';
import AvatarPicker from '@/components/AvatarPicker';

const isIosDevice = () => /iphone|ipad|ipod/i.test(navigator.userAgent || '');
const isStandalonePwa = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;

export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const pricing = usePricing();
  const nav = useNavigate();
  const { t } = useTranslation();
  const fmtAccessDate = (iso) => { if (!iso) return ''; try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }); } catch { return iso; } };
  const [tipsEnabled, setTipsEnabled] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [subBusy, setSubBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingSwitch, setPendingSwitch] = useState(null);
  const [installEvt, setInstallEvt] = useState(null);
  const [smsPhone, setSmsPhone] = useState('');
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [smsConfigured, setSmsConfigured] = useState(true);
  const [smsSaving, setSmsSaving] = useState(false);
  const [showIosSteps, setShowIosSteps] = useState(false);
  const installed = isStandalonePwa();
  const ios = isIosDevice();
  useEffect(() => {
    const h = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener('beforeinstallprompt', h);
    return () => window.removeEventListener('beforeinstallprompt', h);
  }, []);

  useEffect(() => {
    http.get('/me/sms-prefs').then(({ data }) => {
      setSmsPhone(data.phone || '');
      setSmsOptIn(!!data.sms_opt_in);
      setSmsConfigured(data.sms_configured !== false);
    }).catch(() => {});
  }, []);

  const saveSmsPrefs = async (nextOptIn, nextPhone) => {
    const optIn = nextOptIn ?? smsOptIn;
    const phone = (nextPhone ?? smsPhone).trim();
    if (optIn && !phone.startsWith('+')) {
      toast.error('Enter your phone in international format, e.g. +15551234567.');
      return;
    }
    setSmsSaving(true);
    try {
      await http.post('/me/sms-prefs', { phone, sms_opt_in: optIn });
      setSmsOptIn(optIn);
      toast.success(optIn ? 'Daily text reminders on.' : 'Text reminders off.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save.');
    } finally { setSmsSaving(false); }
  };

  const doInstall = async () => {
    if (!installEvt) return;
    installEvt.prompt();
    try { await installEvt.userChoice; } catch { /* ignore */ }
    setInstallEvt(null);
  };
  const trialDaysLeft = (() => {
    try { return Math.max(0, Math.ceil((new Date(user?.trial?.ends_at) - new Date()) / 86400000)); }
    catch { return null; }
  })();

  const saveName = async () => {
    const clean = nameDraft.trim();
    if (!clean) return;
    setSavingName(true);
    try {
      await http.patch('/me', { name: clean });
      await refresh();
      setEditingName(false);
      toast.success('Name updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update your name.');
    } finally { setSavingName(false); }
  };
  const isSupporter = (user?.role || 'member') === 'supporter';

  useEffect(() => {
    http.get('/me/help-status').then(r => setTipsEnabled(!!r.data.smart_tips_enabled)).catch(() => {});
    http.get('/subscription/current').then(r => setSubscription(r.data)).catch(() => setSubscription(null));
  }, []);

  // Returning from a PayPal plan-switch approval → finalize the change.
  useEffect(() => {
    if (searchParams.get('switch') !== 'done') return;
    (async () => {
      try {
        await http.post('/paypal/subscription/sync');
        const r = await http.get('/subscription/current').catch(() => null);
        setSubscription(r?.data || null);
        try { await refresh(); } catch { /* noop */ }
        toast.success('Your plan has been updated.');
      } catch {
        toast.error('We could not finalize the plan change. Please refresh.');
      } finally {
        searchParams.delete('switch');
        setSearchParams(searchParams, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doRevise = async (tierId, interval) => {
    setSubBusy(true);
    try {
      const r = await http.post('/paypal/subscription/revise', { tier_id: tierId, interval });
      if (r.data?.needs_approval && r.data?.approve_url) {
        window.location.href = r.data.approve_url;  // PayPal buyer re-approval
        return;
      }
      const cur = await http.get('/subscription/current').catch(() => null);
      setSubscription(cur?.data || null);
      try { await refresh(); } catch { /* noop */ }
      toast.success('Your plan has been updated.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not change the plan. Please try again.');
    } finally { setSubBusy(false); setPendingSwitch(null); }
  };

  const doCancelSub = async () => {
    if (!window.confirm('Cancel your membership? You will lose access to paid features.')) return;
    setSubBusy(true);
    try {
      await http.post('/subscription/cancel');
      toast.success('Your membership has been cancelled.');
      const r = await http.get('/subscription/current').catch(() => null);
      setSubscription(r?.data || null);
      try { await refresh(); } catch { /* interceptor handles session */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not cancel. Please try again.');
    } finally { setSubBusy(false); }
  };

  const toggleTips = async () => {
    const next = !tipsEnabled;
    setTipsEnabled(next);
    try { await http.post('/me/smart-tips-toggle', { enabled: next }); toast.success(next ? 'Smart Tips on' : 'Smart Tips off'); }
    catch { toast.error('Could not save preference.'); }
  };

  const replayTour = async () => {
    try { await http.post('/me/reset-tour'); } catch (err) {
      // Non-fatal: tour replay is a UX affordance; local dispatch below still fires.
      console.warn('[profile] reset-tour API call failed:', err);
    }
    window.dispatchEvent(new Event('bp:replay-tour'));
    toast.success('Tour restarted');
  };

  return (
    <div className="p-6 lg:px-10 lg:pt-10 space-y-6 lg:space-y-8 lg:max-w-3xl lg:mx-auto" data-testid="profile-page">
      <div className="bg-navy text-white rounded-3xl p-8 lg:p-10 card-soft">
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Profile</p>
        <div className="mt-4 flex items-center gap-5">
          <AvatarPicker />
          <div className="min-w-0 flex-1">
        {editingName ? (
          <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3">
            <input
              data-testid="profile-name-input"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              maxLength={60}
              autoFocus
              className="flex-1 bg-white/10 border border-white/25 rounded-xl px-4 py-2 text-white text-xl font-heading focus:outline-none focus:border-gold"
              placeholder="Your name"
            />
            <div className="flex gap-2">
              <button data-testid="profile-name-save" onClick={saveName} disabled={savingName || !nameDraft.trim()}
                className="bg-gold text-navy rounded-full px-5 py-2 text-sm font-medium hover:bg-gold/90 transition-smooth disabled:opacity-50">
                {savingName ? 'Saving…' : 'Save'}
              </button>
              <button data-testid="profile-name-cancel" onClick={() => setEditingName(false)}
                className="text-white/60 hover:text-white px-3 py-2 text-sm">Cancel</button>
            </div>
          </div>
        ) : (
          <button
            data-testid="profile-name-edit"
            onClick={() => { setNameDraft(user?.name || ''); setEditingName(true); }}
            className="group mt-2 flex items-center gap-3 text-left"
          >
            <h1 className="font-heading text-3xl lg:text-4xl">{user?.name}</h1>
            <Pencil size={18} strokeWidth={1.75} className="text-white/40 group-hover:text-gold transition-colors"/>
          </button>
        )}
        <p className="text-white/60 text-sm lg:text-base">{user?.email}</p>
          </div>
        </div>
      </div>

      {user?.trial?.on_trial && (
        <div className="bg-gold/10 border border-gold/30 rounded-3xl p-5 lg:p-6 card-soft" data-testid="trial-banner">
          <div className="flex items-start gap-3">
            <Gift size={18} className="text-gold mt-0.5 shrink-0"/>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Free trial</p>
              <h2 className="font-heading text-xl lg:text-2xl mt-1">You're on a 7-day free trial</h2>
              <p className="text-sm text-navy/60 mt-1" data-testid="trial-banner-status">
                {trialDaysLeft != null ? `${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left` : 'Trial active'}
                {user.trial.ai_remaining != null && ` · ${user.trial.ai_remaining} AI use${user.trial.ai_remaining === 1 ? '' : 's'} left`}
              </p>
              <p className="text-sm text-navy/55 mt-2">Upgrade anytime for unlimited AI coaching, full courses, the journal, games, and more.</p>
              <button onClick={() => nav('/subscription/checkout')} data-testid="trial-upgrade-btn"
                className="mt-3 text-sm px-5 py-2 rounded-full bg-navy text-white hover:bg-navy/90 transition-smooth">
                Upgrade to full access
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-3xl divide-y divide-navy/5 card-soft">
        <Link to="/app/roadmap" data-testid="profile-roadmap" className="flex items-center justify-between p-5">
          <span className="inline-flex items-center gap-3"><Vote size={16} className="text-gold"/>Vote on the roadmap</span>
          <span className="text-navy/40">›</span>
        </Link>
        <Link to="/app/profile/trust-safety" data-testid="profile-trust-safety" className="flex items-center justify-between p-5">
          <span className="inline-flex items-center gap-3"><Shield size={16} className="text-gold"/>Legal &amp; Privacy · Trust &amp; Safety Center</span>
          <span className="text-navy/40">›</span>
        </Link>
        {!isSupporter && (
          <Link to="/app/therapists" data-testid="profile-therapists" className="flex items-center justify-between p-5"><span>{t('profile.find_therapist')}</span><span className="text-navy/40">›</span></Link>
        )}
        <Link to="/app/memory" data-testid="profile-memory" className="flex items-center justify-between p-5"><span>{t('profile.what_brotherhood_knows')}</span><span className="text-navy/40">›</span></Link>
        <Link to="/crisis" data-testid="profile-crisis" className="flex items-center justify-between p-5 text-red-700"><span>Crisis resources</span><span className="text-red-400">›</span></Link>
        {user?.is_admin && <Link to="/app/admin" data-testid="profile-admin" className="flex items-center justify-between p-5"><span>Admin dashboard</span><span className="text-navy/40">›</span></Link>}
      </div>

      <div className="bg-white rounded-3xl divide-y divide-navy/5 card-soft" data-testid="profile-security">
        <Link to="/app/profile/sessions" data-testid="profile-sessions" className="flex items-center justify-between p-5">
          <span className="inline-flex items-center gap-3"><Laptop size={16} className="text-gold"/>Active sessions</span>
          <span className="text-navy/40">›</span>
        </Link>
        {user?.is_admin && (
          <Link to="/app/admin/security" data-testid="profile-admin-security" className="flex items-center justify-between p-5">
            <span className="inline-flex items-center gap-3"><Shield size={16} className="text-gold"/>Security dashboard</span>
            <span className="text-navy/40">›</span>
          </Link>
        )}
        <button
          data-testid="profile-change-password"
          onClick={async () => {
            const current = window.prompt('Enter your current password');
            if (!current) return;
            const next = window.prompt('Enter a new password (8+ characters)');
            if (!next) return;
            if (next.length < 8) { toast.error('Password must be at least 8 characters'); return; }
            try {
              await http.post('/auth/change-password', { current_password: current, new_password: next });
              toast.success('Password updated. Other devices have been signed out.');
            } catch (e) {
              const d = e?.response?.data?.detail;
              toast.error(typeof d === 'string' ? d : 'Could not change password.');
            }
          }}
          className="w-full text-left p-5 flex items-center justify-between hover:bg-beige/50 transition-smooth"
        >
          <span className="inline-flex items-center gap-3"><KeyRound size={16} className="text-gold"/>Change password</span>
          <span className="text-navy/40">›</span>
        </button>
        {user?.is_admin && (
          <button
            data-testid="profile-change-email"
            onClick={async () => {
              const next = window.prompt('Enter the new admin login email');
              if (!next) return;
              const email = next.trim().toLowerCase();
              if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error('Enter a valid email address.'); return; }
              const current = window.prompt('Confirm your current password to change the login email');
              if (!current) return;
              try {
                await http.post('/auth/change-email', { current_password: current, new_email: email });
                try { await refresh(); } catch { /* noop */ }
                toast.success(`Login email changed to ${email}. Other sessions were signed out.`);
              } catch (e) {
                const d = e?.response?.data?.detail;
                toast.error(typeof d === 'string' ? d : 'Could not change the login email.');
              }
            }}
            className="w-full text-left p-5 flex items-center justify-between hover:bg-beige/50 transition-smooth"
          >
            <span className="inline-flex items-center gap-3"><Shield size={16} className="text-gold"/>Change login email</span>
            <span className="text-navy/40">›</span>
          </button>
        )}
      </div>

      <TwoFactorSetup />
      <TrustedDevicesList />
      <div id="email-prefs" className="scroll-mt-8">
        <EmailPreferences />
      </div>

      {subscription && (subscription.status || subscription.plan_id) && (
        <div className="bg-white rounded-3xl p-6 lg:p-7 card-soft space-y-4" data-testid="profile-membership">
          <div className="flex items-start gap-3">
            <CreditCard size={18} className="text-gold mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Membership</p>
              <h2 className="font-heading text-xl lg:text-2xl mt-1">
                {subscription.plan_id === 'brotherhood_support' ? 'Brotherhood Support' : 'Brotherhood+ Member'}
              </h2>
              <p className="text-sm text-navy/60 mt-1">
                Status:{' '}
                <span data-testid="membership-status" className={subscription.is_active ? 'text-emerald-700 font-medium' : 'text-navy/70 font-medium'}>
                  {subscription.status || 'inactive'}
                </span>
                {subscription.is_comp && ' · complimentary'}
                {subscription.provider === 'paypal' && ' · billed via PayPal'}
                {!subscription.is_comp && subscription.interval && ` · ${subscription.interval}`}
              </p>
              {subscription.is_comp && subscription.is_active && (
                <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-gold/15 text-navy px-3 py-1.5" data-testid="comp-access-badge">
                  <Gift size={14} className="text-gold" />
                  <span className="text-xs font-medium">
                    {subscription.promo_access_expires_at
                      ? `Free access until ${fmtAccessDate(subscription.promo_access_expires_at)}`
                      : 'Complimentary membership · no end date'}
                  </span>
                </div>
              )}
            </div>
          </div>
          {subscription.is_active && subscription.is_comp && (
            <div className="flex flex-wrap items-center gap-2" data-testid="comp-actions">
              <p className="text-sm text-navy/55 w-full">
                {subscription.promo_access_expires_at
                  ? 'Your complimentary access will end on the date above. Continue anytime on a paid plan — no interruption.'
                  : 'Enjoy your complimentary membership. You can move to a paid plan anytime.'}
              </p>
              <button
                onClick={() => nav('/subscription/checkout')}
                className="text-sm px-4 py-2 rounded-full bg-navy text-white hover:bg-navy/90 transition-smooth"
                data-testid="comp-continue-paid-btn"
              >
                Continue on a paid plan
              </button>
            </div>
          )}
          {subscription.is_active && !subscription.is_comp && (
            <div className="flex flex-wrap items-center gap-2">
              {/* Switch tier (Supporter ↔ Member) — changes role/access */}
              <button
                onClick={() => {
                  const to = subscription.plan_id === 'brotherhood_support' ? 'brotherhood_plus' : 'brotherhood_support';
                  setPendingSwitch({ tierId: to, interval: subscription.interval || 'monthly' });
                }}
                disabled={subBusy}
                className="text-sm px-4 py-2 rounded-full bg-navy text-white hover:bg-navy/90 transition-smooth disabled:opacity-50"
                data-testid="switch-tier-btn"
              >
                {subscription.plan_id === 'brotherhood_support' ? 'Upgrade to full Member' : 'Switch to Supporter'}
              </button>
              {/* Switch billing interval (monthly ↔ annual) */}
              <button
                onClick={() => setPendingSwitch({
                  tierId: subscription.plan_id || 'brotherhood_plus',
                  interval: subscription.interval === 'annual' ? 'monthly' : 'annual',
                })}
                disabled={subBusy}
                className="text-sm px-4 py-2 rounded-full bg-white border border-navy/15 text-navy hover:bg-navy/5 transition-smooth disabled:opacity-50"
                data-testid="switch-interval-btn"
              >
                {subscription.interval === 'annual' ? 'Switch to monthly' : 'Switch to annual (save 17%)'}
              </button>
              <button
                onClick={doCancelSub}
                disabled={subBusy}
                className="text-sm px-4 py-2 rounded-full bg-white border border-red-200 text-red-700 hover:bg-red-50 transition-smooth disabled:opacity-50 inline-flex items-center gap-1.5"
                data-testid="cancel-membership-btn"
              >
                <Trash2 size={14} /> {subBusy ? 'Working…' : 'Cancel membership'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Plan-switch confirmation — states PayPal shows the exact prorated amount */}
      {pendingSwitch && (() => {
        const fmt = (blk, fallback) => (blk?.display ? `${blk.display}${blk.period || ''}` : fallback);
        const PRICES = {
          brotherhood_plus: {
            monthly: fmt(pricing?.member?.monthly, '$14.99/month'),
            annual: fmt(pricing?.member?.annual, '$99.99/year'),
          },
          brotherhood_support: {
            monthly: fmt(pricing?.support?.monthly, '$9.99/month'),
            annual: fmt(pricing?.support?.annual, '$69.99/year'),
          },
        };
        const NAMES = { brotherhood_plus: 'Brotherhood+ Member', brotherhood_support: 'Brotherhood Support' };
        const name = NAMES[pendingSwitch.tierId];
        const price = PRICES[pendingSwitch.tierId]?.[pendingSwitch.interval];
        const roleChange = pendingSwitch.tierId !== (subscription?.plan_id);
        return (
          <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !subBusy && setPendingSwitch(null)}>
            <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="switch-confirm-dialog">
              <h3 className="font-heading text-xl text-navy">Switch to {name}?</h3>
              <p className="mt-2 text-sm text-navy/70 leading-relaxed">
                Your plan will change to <span className="font-semibold text-navy">{name} — {price}</span>.
                {roleChange && ' This also changes which sections of the app you can access.'}
              </p>
              <p className="mt-2 text-sm text-navy/70 leading-relaxed">
                PayPal will show your <span className="font-semibold">exact prorated charge</span> on the next screen before you approve — nothing is charged until you confirm there.
              </p>
              <div className="mt-6 flex items-center justify-end gap-2">
                <button
                  onClick={() => setPendingSwitch(null)}
                  disabled={subBusy}
                  className="px-4 py-2 rounded-lg text-sm text-navy/70 hover:bg-navy/5 disabled:opacity-50"
                  data-testid="switch-confirm-cancel"
                >Keep current plan</button>
                <button
                  onClick={() => doRevise(pendingSwitch.tierId, pendingSwitch.interval)}
                  disabled={subBusy}
                  className="px-4 py-2 rounded-lg bg-navy text-white text-sm font-medium hover:bg-navy/90 disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="switch-confirm-continue"
                >{subBusy ? 'Working…' : 'Continue to PayPal'}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Family Access / supporter invites removed per iter52. */}

      <div className="bg-white rounded-3xl divide-y divide-navy/5 card-soft">
        <Link to="/app/help" data-testid="profile-help-center" className="flex items-center justify-between p-5">
          <span className="inline-flex items-center gap-3"><HelpCircle size={16} className="text-gold"/>How to use Brotherhood+</span>
          <span className="text-navy/40">›</span>
        </Link>
        <button data-testid="profile-replay-tour" onClick={replayTour}
          className="w-full text-left p-5 flex items-center justify-between hover:bg-beige/50 transition-smooth">
          <span className="inline-flex items-center gap-3"><RotateCcw size={16}/>Replay guided tour</span>
          <span className="text-xs text-navy/40">Restart</span>
        </button>
        <button data-testid="profile-smart-tips-toggle" onClick={toggleTips}
          className="w-full text-left p-5 flex items-center justify-between hover:bg-beige/50 transition-smooth">
          <span className="inline-flex items-center gap-3"><Lightbulb size={16}/>Smart Tips</span>
          <span className={`text-xs font-medium ${tipsEnabled ? 'text-emerald-700' : 'text-navy/40'}`}>{tipsEnabled ? 'On' : 'Off'}</span>
        </button>
        <button data-testid="profile-send-feedback" onClick={() => window.dispatchEvent(new Event('bp:open-feedback'))}
          className="w-full text-left p-5 flex items-center justify-between hover:bg-beige/50 transition-smooth">
          <span className="inline-flex items-center gap-3"><MessageSquarePlus size={16} className="text-gold"/>Send feedback</span>
          <span className="text-xs text-navy/40">Bug or idea</span>
        </button>
        <a href="#email-prefs" data-testid="profile-notifications-jump"
          onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid=\"email-prefs-panel\"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
          className="p-5 flex items-center justify-between hover:bg-beige/50 transition-smooth cursor-pointer">
          <div className="flex items-center gap-3"><Bell size={16}/><span>Email notifications</span></div>
          <span className="text-xs text-navy/50">Manage</span>
        </a>
        <div className="p-5 flex items-center justify-between">
          <div className="flex items-center gap-3"><Shield size={16}/><span>Privacy & consent</span></div>
          <span className="text-xs text-navy/40">Managed</span>
        </div>
        <div className="p-5 flex items-center justify-between">
          <div className="flex items-center gap-3"><Download size={16}/><span>Download my data</span></div>
          <button data-testid="profile-export" onClick={async ()=>{
            const { data } = await (await import('@/lib/api')).http.get('/me/export');
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = `brotherhood-plus-${Date.now()}.json`; a.click();
            URL.revokeObjectURL(url);
          }} className="text-xs bg-beige text-navy rounded-full px-3 py-1">Export JSON</button>
        </div>
        <div className="p-5 flex items-center justify-between text-red-600">
          <div className="flex items-center gap-3"><Trash2 size={16}/><span>Delete account</span></div>
          <button data-testid="profile-delete" onClick={async ()=>{
            if (!window.confirm('Permanently delete your account and all data? This cannot be undone.')) return;
            await (await import('@/lib/api')).http.delete('/me');
            try { await (await import('@/lib/api')).http.post('/auth/logout'); } catch (err) { console.debug('[profile] logout after account delete failed (non-fatal):', err); }
            window.location.href='/';
          }} className="text-xs bg-red-50 text-red-700 rounded-full px-3 py-1">Delete</button>
        </div>
      </div>

      <div className="bg-white rounded-3xl p-6 lg:p-7 card-soft" data-testid="sms-reminders-section">
        <div className="flex items-center gap-3">
          <Bell size={18} className="text-gold"/>
          <h2 className="font-heading text-lg lg:text-xl">Daily check-in reminders</h2>
        </div>
        <p className="text-sm text-navy/60 mt-2">Get a friendly text each day to log your check-in. Standard rates apply; reply STOP anytime.</p>

        <label className="block mt-4">
          <span className="text-xs uppercase tracking-widest text-navy/60">Mobile number</span>
          <input
            type="tel"
            inputMode="tel"
            value={smsPhone}
            onChange={(e) => setSmsPhone(e.target.value)}
            placeholder="+1 555 123 4567"
            data-testid="sms-phone-input"
            className="mt-1 w-full bg-[#F9F8F6] border border-navy/10 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold"
          />
          <span className="mt-1 block text-[11px] text-navy/45">International format, e.g. +15551234567.</span>
        </label>

        <div className="mt-4 flex items-center justify-between gap-3">
          <span className="text-sm text-navy/75">Text me my daily check-in reminder</span>
          <button
            type="button"
            role="switch"
            aria-checked={smsOptIn}
            disabled={smsSaving}
            onClick={() => saveSmsPrefs(!smsOptIn)}
            data-testid="sms-optin-toggle"
            className={`relative w-12 h-7 rounded-full transition-smooth shrink-0 ${smsOptIn ? 'bg-navy' : 'bg-navy/15'}`}
          >
            <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-all ${smsOptIn ? 'translate-x-5' : ''}`}/>
          </button>
        </div>

        {smsOptIn && (
          <button onClick={() => saveSmsPrefs(true)} disabled={smsSaving} data-testid="sms-save-btn"
            className="mt-4 text-sm px-5 py-2.5 rounded-full bg-navy text-white hover:bg-navy/90 transition-smooth disabled:opacity-50">
            {smsSaving ? 'Saving…' : 'Save number'}
          </button>
        )}

        {!smsConfigured && (
          <p className="mt-3 text-[11px] text-navy/45" data-testid="sms-not-configured">Texts will begin sending once SMS is switched on for Brotherhood+.</p>
        )}
      </div>

      <div className="bg-white rounded-3xl p-6 lg:p-7 card-soft" data-testid="install-app-section">
        <div className="flex items-center gap-3">
          <Download size={18} className="text-gold"/>
          <h2 className="font-heading text-lg lg:text-xl">Add to Home Screen</h2>
        </div>

        {installed ? (
          <p className="text-sm text-emerald-700 mt-2 font-medium" data-testid="install-app-done">
            Brotherhood+ is installed on this device — enjoy the full-screen experience.
          </p>
        ) : (
          <>
            <p className="text-sm text-navy/60 mt-2">Save Brotherhood+ like an app — full screen, one tap away, right from your home screen.</p>

            {/* Primary action — native prompt (Chrome/Android/desktop) or the Safari how-to */}
            <div className="mt-4">
              {installEvt ? (
                <button onClick={doInstall} data-testid="install-app-btn"
                  className="text-sm px-5 py-2.5 rounded-full bg-navy text-white hover:bg-navy/90 transition-smooth inline-flex items-center gap-2">
                  <Download size={14}/> Install now
                </button>
              ) : (
                <button onClick={() => setShowIosSteps((v) => !v)} data-testid="install-app-btn"
                  className="text-sm px-5 py-2.5 rounded-full bg-navy text-white hover:bg-navy/90 transition-smooth inline-flex items-center gap-2">
                  <Share size={14} className="text-gold"/> {ios ? 'Add to Home Screen' : 'How to install'}
                </button>
              )}
            </div>

            {/* Steps — always visible on iOS Safari once tapped, or on non-native browsers */}
            {(showIosSteps || !installEvt) && (
              <div className="mt-4 space-y-2 text-sm text-navy/70 bg-beige/60 rounded-2xl p-4" data-testid="install-app-steps">
                <p className="flex flex-wrap items-center gap-1"><span className="font-semibold">iPhone / iPad (Safari):</span> tap <Share size={13} className="inline text-gold"/> Share, then <span className="font-semibold">Add to Home Screen</span>.</p>
                <p><span className="font-semibold">Android / Chrome:</span> open the menu (⋮) and choose <span className="font-semibold">Install app</span>.</p>
                <p><span className="font-semibold">Desktop:</span> click the install icon in the address bar.</p>
              </div>
            )}
          </>
        )}
      </div>

      <button
        onClick={() => { if (window.confirm('Sign out of Brotherhood+?')) { logout(); nav('/login'); } }}
        data-testid="profile-signout"
        className="w-full flex items-center justify-center gap-2 bg-white border border-navy/10 text-navy rounded-2xl py-4 font-medium hover:bg-red-50 hover:text-red-700 hover:border-red-200 transition-smooth"
      >
        <LogOut size={16} strokeWidth={1.9} /> Sign out
      </button>

      <p className="text-xs text-navy/40 text-center">Brotherhood+ is not therapy or a substitute for professional care.</p>
    </div>
  );
}
